import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Skeleton, Typography } from '@mui/material';
import { SplitTabsPanel } from '@/components/common';
import { BalanceSheet, ProfitLoss } from '@/components/fs-report';
import { BalanceSheetOutlinedIcon, ProfitLossOutlinedIcon } from '@/assets/icons/FsStatementIcon';
import { useFsCategoryStore } from '@/store/audit/fsCategoryStore';
import {
    fsLastYearMapsToPrior,
    mergePriorYearReplaceByFs,
    mergeRemarksReplaceByFs,
    preservePriorGlSplit,
    priorToFsLastYearMaps,
} from '@/pages/components/account/gl-invoice/glLastYearFs';
import {
    GlLastYearBalanceAlert,
    type GlLastYearBsStatus,
} from '@/pages/components/account/gl-invoice/GlLastYearBalanceAlert';
import PriorYearFsExtractBar, {
    type PriorYearPeriodHint,
} from '@/pages/components/account/gl-invoice/PriorYearFsExtractBar';
import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { getAnnual } from '@/services/accountService/companyAnnualService';

const PANEL_HEIGHT = 'calc(100vh - 280px)';

export interface GlLastYearEditorProps {
    companyId: number;
    annualId: number;
    summaryPriorYear: Record<string, number>;
    columnDefs: GlColumnDef[];
    /** 用户改过上年数后回传 GL 原值；首次灌入不回调。 */
    onPriorChange?: (prior: Record<string, number>) => void;
    initialRemarks?: Record<string, string>;
    onRemarksChange?: (remarks: Record<string, string>) => void;
}

/**
 * 用与 FS 相同的资产负债表 / 损益表编辑上年数。离开时还原公司 FS 草稿。
 */
export default function GlLastYearEditor({
    companyId, annualId, summaryPriorYear, columnDefs, onPriorChange,
    initialRemarks, onRemarksChange,
}: GlLastYearEditorProps) {
    const {
        loading: sLoading,
        error: sError,
        initIfNeeded,
        byCode,
        bsTree,
        plTree,
        setBsLastYearValues,
        setPlLastYearValues,
        setBsRetainedEarningsUseStored,
        setGlSuppressPlAfterTaxLastYear,
        setPLComputed,
    } = useFsCategoryStore();

    const snapshotRef = useRef<{
        bsLast: Record<number, string>;
        plLast: Record<number, string>;
        useStored: boolean;
        suppress: boolean;
        afterTaxLastYear: number | null;
    } | null>(null);
    const baselineRef = useRef<string | null>(null);
    /** 保留 GL 分列明细（Sales / Refund）；FS 编辑只改合计时按比例回写。 */
    const detailPriorRef = useRef<Record<string, number>>({ ...summaryPriorYear });
    const onPriorChangeRef = useRef(onPriorChange);
    onPriorChangeRef.current = onPriorChange;
    const onRemarksChangeRef = useRef(onRemarksChange);
    onRemarksChangeRef.current = onRemarksChange;

    const [ready, setReady] = useState(false);
    const [tab, setTab] = useState('BS');
    const [bsError, setBsError] = useState<string | null>(null);
    const [bsStatus, setBsStatus] = useState<GlLastYearBsStatus | null>(null);
    const [editorSeed, setEditorSeed] = useState(0);
    const [expectedPeriod, setExpectedPeriod] = useState<PriorYearPeriodHint | undefined>();
    const [remarksByFsCode, setRemarksByFsCode] = useState<Record<string, string>>(
        () => initialRemarks || {},
    );

    useEffect(() => {
        let cancelled = false;
        if (!annualId) {
            setExpectedPeriod(undefined);
            return;
        }
        getAnnual(annualId)
            .then((a) => {
                if (cancelled || !a) return;
                const from = a.lastYearPeriodFrom || null;
                const to = a.lastYearPeriodTo || null;
                setExpectedPeriod({
                    expectedFrom: from,
                    expectedTo: to,
                    expectedLabel: from || to ? `${from || '?'} ~ ${to || '?'}` : null,
                });
            })
            .catch(() => {
                if (!cancelled) setExpectedPeriod(undefined);
            });
        return () => { cancelled = true; };
    }, [annualId]);

    useEffect(() => {
        if (!companyId || !annualId) return;
        initIfNeeded(companyId, annualId);
    }, [companyId, annualId, initIfNeeded]);

    const byCodeReady = Object.keys(byCode).length > 0;
    useEffect(() => {
        if (!companyId || !annualId || sLoading || !byCodeReady || ready) return;
        const store = useFsCategoryStore.getState();
        snapshotRef.current = {
            bsLast: { ...store.bsLastYearValues },
            plLast: { ...store.plLastYearValues },
            useStored: store.bsRetainedEarningsUseStored,
            suppress: store.glSuppressPlAfterTaxLastYear,
            afterTaxLastYear: store.plAfterTaxLastYear,
        };
        // 与 FS Summary 同源：B6.1 拆分 AL / LFP，Retained=AL−LFP，Profit for the year=LFP
        const { bs, pl, lfpLastYear } = priorToFsLastYearMaps(summaryPriorYear, columnDefs, byCode, {
            plTree: store.plTree,
            bsTree: store.bsTree,
        });
        setBsLastYearValues(bs);
        setPlLastYearValues(pl);
        setBsRetainedEarningsUseStored(true);
        setGlSuppressPlAfterTaxLastYear(false);
        setPLComputed({ afterTaxLastYear: lfpLastYear });
        detailPriorRef.current = { ...summaryPriorYear };
        const seeded = fsLastYearMapsToPrior(bs, pl, byCode, { plAfterTaxLastYear: lfpLastYear });
        baselineRef.current = JSON.stringify(seeded);
        setReady(true);
    }, [
        companyId, annualId, sLoading, byCodeReady, ready, byCode,
        summaryPriorYear, columnDefs,
        setBsLastYearValues, setPlLastYearValues,
        setBsRetainedEarningsUseStored, setGlSuppressPlAfterTaxLastYear, setPLComputed,
    ]);

    useEffect(() => {
        if (!ready) return;
        return useFsCategoryStore.subscribe((store) => {
            const collapsed = fsLastYearMapsToPrior(
                store.bsLastYearValues,
                store.plLastYearValues,
                store.byCode,
                { plAfterTaxLastYear: store.plAfterTaxLastYear },
            );
            const json = JSON.stringify(collapsed);
            if (baselineRef.current == null || json === baselineRef.current) return;
            const preserved = preservePriorGlSplit(collapsed, detailPriorRef.current);
            detailPriorRef.current = preserved;
            onPriorChangeRef.current?.(preserved);
        });
    }, [ready]);

    useEffect(() => () => {
        const snap = snapshotRef.current;
        if (!snap) return;
        const store = useFsCategoryStore.getState();
        store.setBsLastYearValues(snap.bsLast);
        store.setPlLastYearValues(snap.plLast);
        store.setBsRetainedEarningsUseStored(snap.useStored);
        store.setGlSuppressPlAfterTaxLastYear(snap.suppress);
        store.setPLComputed({ afterTaxLastYear: snap.afterTaxLastYear });
    }, []);

    const handleBsErrorChange = useCallback((msg: string | null) => {
        setBsError(msg || null);
    }, []);
    const handleBsStatusChange = useCallback((s: GlLastYearBsStatus) => {
        setBsStatus(s);
    }, []);

    const handleExtractApply = useCallback((
        extracted: Record<string, number>,
        _result?: unknown,
        remarks?: Record<string, string>,
    ) => {
        const store = useFsCategoryStore.getState();
        const normalized: Record<string, number> = {};
        for (const [k, v] of Object.entries(extracted || {})) {
            const n = Number(v);
            if (k && Number.isFinite(n)) normalized[k] = n;
        }
        const touchedFs = new Set<string>();
        for (const k of Object.keys(normalized)) {
            const i = k.indexOf('|');
            if (i > 0) touchedFs.add(k.slice(0, i));
        }
        // remarks 含改 FS 后需清空的旧科目（空串）
        for (const fs of Object.keys(remarks || {})) {
            if (fs) touchedFs.add(fs);
        }
        setRemarksByFsCode((prev) => {
            const next = mergeRemarksReplaceByFs(prev, remarks, touchedFs);
            onRemarksChangeRef.current?.(next);
            return next;
        });
        const current = ready
            ? detailPriorRef.current
            : { ...summaryPriorYear };
        // 同 FS 旧 key（|LY / 旧 gl）清掉再写，避免重新识别翻倍
        const merged = mergePriorYearReplaceByFs(current, normalized);
        detailPriorRef.current = merged;
        const { bs: bsMap, pl: plMap, lfpLastYear } = priorToFsLastYearMaps(
            merged,
            columnDefs,
            store.byCode,
            { plTree: store.plTree, bsTree: store.bsTree },
        );
        const nextBs = { ...(store.bsLastYearValues || {}), ...bsMap };
        const nextPl = { ...(store.plLastYearValues || {}), ...plMap };
        setBsLastYearValues(nextBs);
        setPlLastYearValues(nextPl);
        setBsRetainedEarningsUseStored(true);
        setGlSuppressPlAfterTaxLastYear(false);
        setPLComputed({ afterTaxLastYear: lfpLastYear });
        // baseline 用 FS 合并视图；落库明细保留 merged 分列（Sales / Refund）
        const seeded = fsLastYearMapsToPrior(nextBs, nextPl, store.byCode, { plAfterTaxLastYear: lfpLastYear });
        baselineRef.current = JSON.stringify(seeded);
        onPriorChangeRef.current?.(merged);
        setEditorSeed((n) => n + 1);
    }, [
        ready, summaryPriorYear, columnDefs,
        setBsLastYearValues, setPlLastYearValues,
        setBsRetainedEarningsUseStored, setGlSuppressPlAfterTaxLastYear, setPLComputed,
    ]);

    const bs = useMemo(() => (
        <BalanceSheet
            key={`ly-bs-${editorSeed}`}
            mode="fs"
            annualId={annualId}
            lastYearOnly
            lastYearRemarkByFsCode={remarksByFsCode}
            onErrorChange={handleBsErrorChange}
            onStatusChange={handleBsStatusChange}
        />
    ), [annualId, editorSeed, remarksByFsCode, handleBsErrorChange, handleBsStatusChange]);
    const pl = useMemo(() => (
        <ProfitLoss
            key={`ly-pl-${editorSeed}`}
            mode="fs"
            annualId={annualId}
            lastYearOnly
            lastYearRemarkByFsCode={remarksByFsCode}
        />
    ), [annualId, editorSeed, remarksByFsCode]);

    const tabs = useMemo(() => ([
        { key: 'BS', label: 'Balance Sheet', icon: <BalanceSheetOutlinedIcon sx={{ fontSize: 24 }} />, content: bs },
        { key: 'PL', label: 'Income Statement', icon: <ProfitLossOutlinedIcon sx={{ fontSize: 24 }} />, content: pl },
    ]), [bs, pl]);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Edit comparative figures the same way as the financial statements. Save writes them back with the invoices.
            </Typography>
            <PriorYearFsExtractBar
                onApply={handleExtractApply}
                disabled={!ready || !!sError}
                expectedPeriod={expectedPeriod}
                companyId={companyId}
            />
            {sError && <Alert severity="error" sx={{ mb: 1 }}>{sError}</Alert>}
            {ready && !sError && <GlLastYearBalanceAlert errorMsg={bsError} status={bsStatus} />}
            {(sLoading || !ready) && (
                <Box sx={{ p: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)}
                </Box>
            )}
            {ready && !sError && ((bsTree?.length ?? 0) > 0 || (plTree?.length ?? 0) > 0) && (
                <SplitTabsPanel
                    tabs={tabs}
                    height={PANEL_HEIGHT}
                    selectedKey={tab}
                    onChange={setTab}
                    renderAll
                    allowMaximize={false}
                />
            )}
        </Box>
    );
}
